import { LOGO_CONCEPTS, LOGO_CONCEPTS_V2, LOGO_CONCEPTS_V3 } from "../components/LogoConcepts";
import { LogoWordmark } from "../components/Logo";

/**
 * Internal logo lab — preview every MS monogram concept side-by-side at
 * three sizes (favicon, header, hero) on light + dark surfaces. Used to
 * pick the final brand mark before regenerating the public assets.
 *
 * Round 2 (top of page) = Ligature × Capsule hybrids based on user feedback.
 * Round 1 (below) = original 6 concepts kept for reference.
 */

function ConceptCard({ id, name, Component, blurb }) {
  return (
    <section
      data-testid={`logo-concept-${id}`}
      className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm"
    >
      <header className="flex items-baseline justify-between gap-4">
        <h2 className="text-lg font-semibold text-stone-900">{name}</h2>
        <span className="text-xs uppercase tracking-wider text-stone-400">
          #{id}
        </span>
      </header>
      <p className="mt-1 text-sm text-stone-600">{blurb}</p>

      <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Light surface */}
        <div className="rounded-xl bg-[#fdf8f3] p-6">
          <p className="mb-4 text-[11px] uppercase tracking-wider text-stone-500">
            On cream
          </p>
          <div className="flex flex-wrap items-end gap-8">
            <div className="flex flex-col items-center gap-2">
              <Component color="#111" accent="#22c55e" className="h-4 w-4" />
              <span className="text-[10px] text-stone-500">16px</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center gap-2">
                <Component color="#111" accent="#22c55e" className="h-9 w-9" />
                <LogoWordmark color="#111" size={18} />
              </div>
              <span className="text-[10px] text-stone-500">header</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Component color="#111" accent="#22c55e" className="h-20 w-20" />
              <span className="text-[10px] text-stone-500">80px</span>
            </div>
          </div>
        </div>

        {/* Dark surface */}
        <div className="rounded-xl bg-stone-900 p-6">
          <p className="mb-4 text-[11px] uppercase tracking-wider text-stone-400">
            On dark
          </p>
          <div className="flex flex-wrap items-end gap-8">
            <div className="flex flex-col items-center gap-2">
              <Component color="#fdf8f3" accent="#86efac" className="h-4 w-4" />
              <span className="text-[10px] text-stone-500">16px</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center gap-2">
                <Component color="#fdf8f3" accent="#86efac" className="h-9 w-9" />
                <LogoWordmark color="#fdf8f3" size={18} />
              </div>
              <span className="text-[10px] text-stone-500">header</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Component color="#fdf8f3" accent="#86efac" className="h-20 w-20" />
              <span className="text-[10px] text-stone-500">80px</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Logos() {
  return (
    <main className="min-h-screen bg-[#fdf8f3] px-6 py-16">
      <div className="mx-auto max-w-5xl">
        <h1 className="text-3xl font-semibold tracking-tight text-stone-900">
          Logo lab
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-stone-600">
          Each mark is shown at three sizes (16px favicon · header · 80px) on
          cream and dark surfaces.
        </p>

        {/* Round 3 — Reference-style monoline */}
        <div className="mt-12">
          <div className="mb-6 flex items-baseline justify-between">
            <h2 className="text-xl font-semibold tracking-tight text-stone-900">
              Round 3 — Monoline droplet (live brand)
            </h2>
            <span className="text-xs uppercase tracking-wider text-emerald-700">
              ✦ live · pick the variant
            </span>
          </div>
          <p className="mb-6 max-w-2xl text-sm text-stone-600">
            Pure thin black line, no colors. M-topped droplet outline with an
            inner S curl and a small medical cross. Variants tweak peak shape
            and inner detail. <strong>V1 is currently locked in</strong> — tell
            me a different one to switch.
          </p>
          <div className="space-y-8">
            {LOGO_CONCEPTS_V3.map((c) => (
              <ConceptCard key={c.id} {...c} />
            ))}
          </div>
        </div>

        {/* Round 2 — Ligature × Capsule hybrids */}
        <div className="mt-12">
          <div className="mb-6 flex items-baseline justify-between">
            <h2 className="text-xl font-semibold tracking-tight text-stone-900">
              Round 2 — Ligature × Capsule hybrids
            </h2>
            <span className="text-xs uppercase tracking-wider text-emerald-700">
              ✦ new based on your feedback
            </span>
          </div>
          <p className="mb-6 max-w-2xl text-sm text-stone-600">
            These keep the calligraphic MS-ligature flow you liked, while making
            the <strong>S equally visible</strong> and weaving in a real pill /
            capsule cue (seams, tube strokes, capsule bowls).
          </p>
          <div className="space-y-8">
            {LOGO_CONCEPTS_V2.map((c) => (
              <ConceptCard key={c.id} {...c} />
            ))}
          </div>
        </div>

        {/* Round 1 — original concepts (kept for reference) */}
        <div className="mt-16">
          <div className="mb-6 flex items-baseline justify-between">
            <h2 className="text-xl font-semibold tracking-tight text-stone-900">
              Round 1 — Original concepts (reference)
            </h2>
            <span className="text-xs uppercase tracking-wider text-stone-400">
              archive
            </span>
          </div>
          <div className="space-y-8 opacity-90">
            {LOGO_CONCEPTS.map((c) => (
              <ConceptCard key={c.id} {...c} />
            ))}
          </div>
        </div>

        <p className="mt-12 text-sm text-stone-500">
          Tell me the variant you'd like (e.g. "V3 Twin Pills + S Drape") and
          I'll lock it in and regenerate the favicons + OG images.
        </p>
      </div>
    </main>
  );
}
